import React, { useState, useEffect, useRef } from "react";
import "./TopBar.css"; // Ensure you have the appropriate CSS for styling

export default function TopBar({
  notifRef,
  msgRef,
  requests = [],
  requested = [],
  friends = [],
  showRequests,
  setShowRequests,
  handleLogout,
  setShowSidebar,
  onSearch,
  children,
  logo // Add logo prop
}) {
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isMobile, setIsMobile] = useState(false);
  const searchInputRef = useRef(null);

  // Check screen size
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Focus search input when shown
  useEffect(() => {
    if (showSearch && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [showSearch]);

  const handleSearch = () => {
    if (searchQuery.trim()) {
      onSearch?.(searchQuery);
      setSearchQuery("");
      setShowSearch(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    } else if (e.key === 'Escape') {
      setShowSearch(false);
      setSearchQuery("");
    }
  };

  const totalNotifications = requests.length + requested.length;
  const totalMessages = friends.length;

  return (
    <header className="topbar">
      {/* Logo Section */}
      <div className="topbar-brand">
        <div className="logo-container">
          {logo ? (
            <img 
              src={logo} 
              alt="RazChatz Logo" 
              className="logo-image"
            />
          ) : (
            <div className="logo-placeholder">R</div>
          )}
          <span className="brand-text">RazChatz</span>
        </div>
      </div>

      {/* Search Section - Desktop */}
      {!isMobile && (
        <div className="topbar-search-desktop">
          <div className="search-container">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder="Search users..."
              className="search-input"
            />
            <button 
              className="search-btn" 
              onClick={handleSearch}
              disabled={!searchQuery.trim()}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M21 21l-4.35-4.35M17 10a7 7 0 11-14 0 7 7 0 0114 0z" stroke="currentColor" strokeWidth="2" fill="none"/>
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Actions Section */}
      <div className="topbar-actions">
        {/* Mobile Search Toggle */}
        {isMobile && (
          <button
            className={`action-btn ${showSearch ? 'active' : ''}`}
            onClick={() => setShowSearch(!showSearch)}
            aria-label="Toggle search"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M21 21l-4.35-4.35M17 10a7 7 0 11-14 0 7 7 0 0114 0z" stroke="currentColor" strokeWidth="2" fill="none"/>
            </svg>
          </button>
        )}

        {/* Notifications */}
        <div ref={notifRef} className="action-item">
          <button
            className="action-btn"
            onClick={() => setShowRequests?.(v => !v)}
            aria-label={`Notifications ${totalNotifications > 0 ? `(${totalNotifications})` : ''}`}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 0 1-3.46 0" stroke="currentColor" strokeWidth="2" fill="none"/>
            </svg>
            {totalNotifications > 0 && (
              <span className="badge">{totalNotifications > 99 ? '99+' : totalNotifications}</span>
            )}
          </button>
          {children}
        </div>

        {/* Messages */}
        <div ref={msgRef} className="action-item">
          <button
            className="action-btn"
            onClick={() => setShowSidebar?.(v => !v)}
            aria-label={`Messages ${totalMessages > 0 ? `(${totalMessages})` : ''}`}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" stroke="currentColor" strokeWidth="2" fill="none"/>
              <polyline points="22,6 12,13 2,6" stroke="currentColor" strokeWidth="2" fill="none"/>
            </svg>
            {totalMessages > 0 && (
              <span className="badge">{totalMessages > 99 ? '99+' : totalMessages}</span>
            )}
          </button>
        </div>

        {/* Logout */}
        <button
          className="logout-btn"
          onClick={handleLogout}
          aria-label="Logout"
        >
          {isMobile ? (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" stroke="currentColor" strokeWidth="2" fill="none"/>
            </svg>
          ) : (
            'Logout'
          )}
        </button>
      </div>

      {/* Mobile Search Overlay */}
      {isMobile && showSearch && (
        <div className="search-overlay">
          <div className="search-overlay-content">
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder="Search users..."
              className="search-overlay-input"
            />
            <button 
              className="search-overlay-btn" 
              onClick={handleSearch}
              disabled={!searchQuery.trim()}
            >
              Search
            </button>
            <button 
              className="search-overlay-cancel" 
              onClick={() => {
                setShowSearch(false);
                setSearchQuery("");
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </header>
  );    
  }